import React, { useState } from 'react';
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import CommonTable from '../../../global/table';
import CommonDialog from '../../../global/dialog';
import NoRecordFound from '../../../global/noRecordFound';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import { useQueryClient } from '@tanstack/react-query';
import SOURCES from '../../../../utils/constants';
import { capitalizeFirstLetter } from '../../../../utils/functions';
import CommonCardView from '../../../global/cardView';
import CommonDrawer from '../../../global/drawer';
import EnhancedAlertGridView from './enhancedAlertGridView';
import EnhancedAlertDetails from './enhancedAlertDetails';

const EnhancedAlertList = ({ alerts, isLoading, sort, setSort, viewType, setEditAlert, keyParams }) => {
    const [toggleModal, setToggleModal] = useState({ show: false, id: null });
    const [detailsDrawer, setDetailsDrawer] = useState({ show: false, data: null });
    const queryClient = useQueryClient();
    const { ALERT_VIA_EMAIL, ALERT_VIA_SMS, ALERT_VIA_BOTH } = SOURCES;

    const { mutate: deleteAlert, isPending: deleteMutationPending } = useGenericMutation({
        endpoint: "alert/delete",
        method: "POST",
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.alerts.lists(keyParams),
                type: "active"
            });
            setToggleModal({ show: false, id: null });
        },
        successMessage: "Alert deleted successfully"
    });

    const handleDelete = () => {
        deleteAlert({ id: toggleModal.id });
    };

    const handleDetails = (record) => {
        setDetailsDrawer({ show: true, data: record });
    };

    const columns = [
        {
            title: "Alert Via",
            dataIndex: "alertVia",
            key: "alertVia",
            sortType: "alertVia",
            render: (text) => (
                <span>
                    {text === ALERT_VIA_BOTH
                        ? `${capitalizeFirstLetter(ALERT_VIA_EMAIL)} / ${capitalizeFirstLetter(ALERT_VIA_SMS)}`
                        : capitalizeFirstLetter(text)}
                </span>
            )
        },
        {
            title: "Name",
            dataIndex: "alertName",
            key: "alertName",
            sortType: "alertName",
            render: (text, record) => (
                <span className="text-primary cursor-pointer hover:underline font-medium" onClick={() => setEditAlert({ show: true, id: record._id })}>
                    {text}
                </span>
            )
        },
        {
            title: "Email",
            dataIndex: "alertEmail",
            key: "alertEmail",
            sortType: "alertEmail"
        },
        {
            title: "Mobile No",
            dataIndex: "alertSms",
            key: "alertSms",
            sortType: "alertSms"
        }
    ];

    const actions = [
        {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: (record) => setEditAlert({ show: true, id: record._id }),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white",
        },
        {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: (record) => setToggleModal({ show: true, id: record._id }),
            className: "!text-red-500 !border-red-500 hover:!border-red-500 hover:!bg-red-500 hover:!text-white",
        }
    ];

    const data = Array.isArray(alerts) ? alerts.map(item => ({ ...item, key: item._id, id: item._id })) : [];

    return (
        <>
            {data.length > 0 ? (
                <>
                    {viewType === "LIST" ? (
                        <CommonTable
                            columns={columns}
                            data={data}
                            showActions={true}
                            actions={actions}
                            sort={sort}
                            setSort={setSort}
                            isLoading={isLoading}
                            onRowClick={(record) => handleDetails(record)}
                        />
                    ) : (
                        <CommonCardView>
                            {data.map((record) => (
                                <EnhancedAlertGridView
                                    key={record.id}
                                    record={record}
                                    actions={actions}
                                    onCardClick={() => handleDetails(record)}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                <NoRecordFound message="No alerts found." />
            )}

            {detailsDrawer.show && (
                <CommonDrawer
                    open={detailsDrawer.show}
                    setOpen={(val) => setDetailsDrawer(prev => ({ ...prev, show: val }))}
                    title="Alert Details"
                    width="600px"
                >
                    <EnhancedAlertDetails data={detailsDrawer.data} />
                </CommonDrawer>
            )}

            {toggleModal.show && (
                <CommonDialog
                    title="Delete Alert"
                    description="Are you sure you want to delete this alert?"
                    open={toggleModal.show}
                    setOpen={(val) => setToggleModal(prev => ({ ...prev, show: val }))}
                    onOk={handleDelete}
                    onCancel={() => setToggleModal({ show: false, id: null })}
                    isConfirmButtonLoading={deleteMutationPending}
                    okButtonTitle="Delete"
                />
            )}
        </>
    );
};

export default EnhancedAlertList;
